import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Gavel as GavelIcon,
  Calculate as CalculateIcon,
  DateRange as DateRangeIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { CONVERSION_TABLE_DATA } from "../../Config/constants";

// Icons for each period type
const periodIcons = [
  <DateRangeIcon key="1" />,
  <DateRangeIcon key="2" />,
  <DateRangeIcon key="3" />,
  <DateRangeIcon key="4" />,
];

const ResultsTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        bgcolor: "grey.100",
        pt: { xs: 4, md: 8 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Legal Reference Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            bgcolor: "secondary.main",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "secondary.dark",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <GavelIcon
              sx={{
                fontSize: 32,
                color: "primary.main",
                mt: 0.5,
              }}
            />
            <Box>
              <Typography
                variant="h6"
                color="primary.main"
                fontWeight={700}
                gutterBottom
              >
                Fundamento Legal
              </Typography>
              <Typography variant="body1" color="primary.main" align="justify">
                Los puntajes totales, se convertirán a escala de 1 a 100, para dar
                aplicación al{" "}
                <strong>Acuerdo N° 19 del 14 de diciembre de 2018</strong>{" "}
                sobre el puntaje mínimo de{" "}
                <Chip
                  label="40/100 para programas presenciales"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    fontWeight: 600,
                    mx: 0.5,
                  }}
                />{" "}
                y {" "}
                <Chip
                  label="30/100 para programas a distancia"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    fontWeight: 600,
                    mx: 0.5,
                  }}
                />
                .
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Section Title */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            color="text.primary"
            gutterBottom
          >
            Tabla de Conversión
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Referencia oficial para la conversión de puntajes según el periodo de
            presentación
          </Typography>
        </Box>

        {/* Mobile: Card Layout */}
        {isMobile ? (
          <Grid container spacing={2}>
            {CONVERSION_TABLE_DATA.map((row, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {periodIcons[index]}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {row.period}
                    </Typography>
                  </Box>
                  <CardContent>
                    {/* Scale */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                      >
                        <SpeedIcon fontSize="small" color="action" />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          textTransform="uppercase"
                        >
                          Escala de resultado
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.primary">
                        {row.scale}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Conversion */}
                    <Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                      >
                        <CalculateIcon fontSize="small" color="action" />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          textTransform="uppercase"
                        >
                          Conversión a escala 1-100
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          bgcolor: "success.light",
                          p: 1.5,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        {row.conversion}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* Desktop: Enhanced Table */
          <TableContainer
            component={Paper}
            elevation={3}
            sx={{ borderRadius: 3, overflow: "hidden" }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.main" }}>
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DateRangeIcon sx={{ color: "white" }} />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="white"
                      >
                        Periodo de realización de la prueba
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SpeedIcon sx={{ color: "white" }} />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="white"
                      >
                        Escala de resultado
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalculateIcon sx={{ color: "white" }} />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="white"
                      >
                        Conversión a escala de 1 a 100
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {CONVERSION_TABLE_DATA.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": { bgcolor: "grey.50" },
                      "&:hover": { bgcolor: "action.hover" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell sx={{ py: 2.5, verticalAlign: "top" }}>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        color="text.primary"
                      >
                        {row.period}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5, verticalAlign: "top" }}>
                      <Typography variant="body1" color="text.secondary">
                        {row.scale}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5, verticalAlign: "top" }}>
                      <Box
                        sx={{
                          bgcolor: "success.light",
                          p: 1.5,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <Typography variant="body1" color="success.dark">
                          {row.conversion}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default ResultsTable;
